import api from "../services/api";

/**
 * Patient Referral Link Utilities
 *
 * Providers generate referral links that encode their identity and clinic info.
 * When a patient accesses the peptide section, the referral data is read from
 * either the URL (?ref= parameter) or from their session (if previously stored).
 *
 * SECURITY: The referral link is a convenience — the backend MUST independently
 * validate that the referringProviderId exists and is authorized to refer patients.
 * The frontend link is not cryptographically signed.
 */

/**
 * Build a referral URL that a provider sends to a patient.
 * The patient opens this link, logs in, and is directed to the peptide catalog
 * with the provider's info pre-loaded.
 */
export const buildPatientReferralUrl = (providerData) => {
  const payload = {
    referringProviderId: providerData.providerId,
    referringClinicName: providerData.clinicName || providerData.orgName,
    referringClinicAddress: providerData.clinicAddress || {},
    providerName: providerData.providerName,
    providerEmail: providerData.providerEmail,
    orgId: providerData.orgId,
    ts: Date.now(),
  };
  const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  return `${window.location.origin}/peptides?ref=${encodeURIComponent(encoded)}`;
};

/**
 * Parse a referral parameter from the URL.
 * Returns the decoded referral data or null if invalid/missing.
 */
// Referral links expire after 90 days
const REFERRAL_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export const parseReferralParam = (searchString) => {
  try {
    const refParam = new URLSearchParams(searchString).get("ref");
    if (!refParam) return null;
    const decoded = JSON.parse(decodeURIComponent(escape(atob(refParam))));
    // Basic validation — must have a provider ID
    if (!decoded.referringProviderId) return null;
    // Expiry check — reject referral links older than 90 days. Fail-closed
    // when `ts` is missing entirely or non-finite: a handcrafted payload
    // with no timestamp would otherwise skip the TTL gate and remain valid
    // forever. `Number.isFinite` (not `typeof === "number"`) is required:
    // `typeof NaN === "number"` is true and any comparison against NaN is
    // false, so a `{ ts: NaN }` payload slips a `typeof` check but
    // `Number.isFinite(NaN) === false` correctly rejects it.
    if (!Number.isFinite(decoded.ts) || Date.now() - decoded.ts > REFERRAL_TTL_MS) {
      console.warn("Referral link expired or missing timestamp");
      return null;
    }
    return decoded;
  } catch (err) {
    console.error("Error parsing referral data:", err);
    return null;
  }
};

/**
 * Store referral data in sessionStorage so it persists across page navigation
 * but not across browser sessions.
 */
const REFERRAL_STORAGE_KEY = "peptide_referral";

export const storeReferralData = (referralData) => {
  try {
    sessionStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
  } catch (e) {
    console.error("Failed to store referral data:", e);
  }
};

export const getStoredReferralData = () => {
  try {
    const stored = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (!data.referringProviderId) return null;
    return data;
  } catch (e) {
    return null;
  }
};

export const clearStoredReferralData = () => {
  sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
};

/**
 * Fetch referral data from the patient's authenticated session.
 *
 * Calls `Clinical Profile - Self - Get` (patient-scope read endpoint added
 * in lucasai-backend PR #47), which returns a `referringProvider` snapshot
 * captured at signup from `user.custom.clinicalProfileLink`. The backend
 * derives the provider entirely from the session — never trust a
 * client-supplied providerOrgId.
 *
 * Returns null on missing linkage, malformed response, or any error so the
 * caller can fall through to the existing empty-state UI.
 */
export const fetchReferralFromSession = async () => {
  try {
    const res = await api.getMyClinicalProfile();
    const rp = res?.data?.output?.referringProvider;
    if (!rp?.referringProviderId) return null;
    return {
      referringProviderId: rp.referringProviderId,
      referringClinicName: rp.referringClinicName,
      referringClinicAddress: rp.referringClinicAddress || {},
      providerName: rp.providerName,
      providerEmail: rp.providerEmail,
      orgId: rp.orgId,
      // Stamp fresh — session-hydrated referrals are intentionally exempt
      // from the 90-day TTL that applies to URL-decoded `?ref=` links.
      // The session bounds them: useAuth.logout calls clearStoredReferralData,
      // and tab close clears sessionStorage. No long-lived cross-account leak.
      ts: Date.now(),
    };
  } catch (err) {
    console.warn("Failed to hydrate referral data from session:", err);
    return null;
  }
};
